import React, { useEffect, useState } from "react"
import { Alert, Button, FormFeedback, FormGroup, FormText, Input, Label, ModalBody, Table } from "reactstrap"
import { get } from "helpers/api_helper"
import { firstOffer, offeredName, placeName, quantityLabel, supplierNames } from "./labels"
import { SHIPMENT_WEIGHT_LIMIT, itemKey, itemName, productsWeightOf, reasonHint, returnedTotals, sentItems, shipmentName, statedWeight, stockEffectLabel,
  unitOf, weightLabel } from "./shipmentLabels"
import DraftModal from "./DraftModal"
import { todayIn } from "pages/Logistics/Stock/stockForm"
import TransportFields, { checkTransport, transportBody, transportForm } from "./TransportFields"

// ——— The dialogs after the main shipment's validation (US 83–94): corrections, returns and the cancellation ———
// Each change takes a reason, optional for the correction of how the shipment left (T5, Tudor 2026-10-06), and is bound to the shipped
// sheet as it was read; a refusal stays in the dialog.

export const QUANTITY_LIMIT = 1000000
const whole = value => /^\d+$/.test(String(value).trim()) && Number(value) <= QUANTITY_LIMIT
export const beforeInventory = "Validated before the initial inventory: the stock does not change."
// What a cancellation does to an AWB Cargus generated (US 103, ticket 20).
export const GENERATED_CANCELLATION = "Cargus generated this AWB: the cancellation deletes it at Cargus, which is possible only before the courier "
  + "scans the parcel. Once scanned, record a return instead."

// `dirty`: what was typed would be lost, so closing asks first (DraftModal); `hint`: what the footer's button waits for (`reasonHint`,
// shipmentLabels.js).
export function AfterShipmentDialog({ id, title, dirty, failure, onClose, closeLabel, actions, hint, size, children }) {
  return <DraftModal id={id} title={title} size={size} dirty={dirty} onClose={onClose} closeLabel={closeLabel} actions={actions} hint={hint}>
    <ModalBody>
      {children}
      {failure && <Alert color="danger" className="mt-3 mb-0">{failure}</Alert>}
    </ModalBody>
  </DraftModal>
}

// The reason of a command after a shipment: required, except where a resend or a correction of the transport, the address or the
// contact leaves it `optional`.
export function ReasonField({ id, value, onChange, placeholder, hint, optional = false }) {
  return <FormGroup className="mb-0">
    <Label for={id}>{optional ? "Reason (optional)" : <>Reason <span className="text-danger">*</span></>}</Label>
    <Input id={id} maxLength={500} value={value} placeholder={placeholder} onChange={event => onChange(event.target.value)} />
    {hint && <FormText>{hint}</FormText>}
  </FormGroup>
}

// The places of a locked line (a line without places has one, null), each with its locked quantity.
const lockedPlaces = line => (line.variants ? line.variants.map(({ place, effective }) => ({ place, quantity: effective }))
  : [{ place: null, quantity: line.effective }])
const lockedLabel = line => `Locked at validation: ${lockedPlaces(line).map(({ place, quantity }) => [place && placeName(place), quantityLabel(quantity, line.unit)]
  .filter(Boolean).join(" ")).join(" · ")}`
// What MPG sends of a line at each place, with a supplier: only MPG takes goods out of the warehouse, and only catalogue products.
const sentBy = (supplier, line, quantity) => (supplier === "mpg" && line.product ? quantity : 0)

// One quantity per place, or one for a line without places.
function PlaceQuantities({ id, places, values, onChange }) {
  return <div className="form-row">{places.map(place => {
    const key = place ?? ""
    return <FormGroup key={key} className="col-sm-4">
      <Label for={`${id}-${key || "line"}`}>{place ? placeName(place) : "Quantity"}</Label>
      <Input id={`${id}-${key || "line"}`} type="number" min={0} step={1} value={values[key] ?? ""} invalid={values[key] !== undefined && values[key] !== ""
        && !whole(values[key])} onChange={event => onChange({ ...values, [key]: event.target.value })} />
    </FormGroup>
  })}</div>
}

// What a change of one line, or of the product it adds (`line`), takes from the stock or brings back, with the stock after (`balances`, D22).
function StockEffect({ movesStock, moves, line, balances }) {
  return <>
    <p className="small mb-1"><i className="bx bx-package mr-1 text-muted" aria-hidden="true" />
      {movesStock ? stockEffectLabel(moves, line, balances) : beforeInventory}</p>
    <p className="small text-muted">No new AWB. To send more, create a supplementary shipment.</p>
  </>
}

// „Correction after shipment” of a line (US 83, ticket 13 Q12): the quantities really in the parcel (per place), the supplier and the
// notes. A line passing from MPG to the club brings its goods back to the stock, and the other way takes them out.
export function CorrectLineDialog({ line, name, movesStock, balances, pending, failure, onSubmit, onClose }) {
  const locked = lockedPlaces(line)
  const [values, setValues] = useState(() => Object.fromEntries(locked.map(({ place, quantity }) => [place ?? "", String(quantity)])))
  const [supplier, setSupplier] = useState(line.supplier)
  const [notes, setNotes] = useState(line.notes || "")
  const [reason, setReason] = useState("")
  const valid = locked.every(({ place }) => whole(values[place ?? ""]))
  const quantities = locked.map(({ place }) => ({ place, quantity: Number(values[place ?? ""]) }))
  const quantitiesChanged = valid && quantities.some(({ quantity }, index) => quantity !== locked[index].quantity)
  const changed = quantitiesChanged || supplier !== line.supplier || notes.trim() !== (line.notes || "")
  const moves = valid ? locked.map(({ place, quantity }, index) => ({ place,
    quantity: sentBy(line.supplier, line, quantity) - sentBy(supplier, line, quantities[index].quantity) })).filter(({ quantity }) => quantity) : []
  const submit = () => onSubmit({ action: "correctLine", line: line.key, ...(quantitiesChanged ? { quantities } : {}),
    ...(supplier !== line.supplier ? { supplier } : {}), ...(notes.trim() !== (line.notes || "") ? { notes: notes.trim() } : {}), reason: reason.trim() })
  return <AfterShipmentDialog id="logistics-correct-line-title" title={`Correction after shipment — ${name}`} dirty={changed || Boolean(reason.trim())}
    failure={failure} onClose={onClose} hint={reasonHint(reason, "save the correction")} actions={<Button color="primary" disabled={pending || !valid || !changed || !reason.trim()} onClick={submit}>{pending ? "Saving…" : "Save correction"}</Button>}>
    <p className="mb-2">{lockedLabel(line)}</p>
    <PlaceQuantities id="logistics-correct-quantity" places={locked.map(({ place }) => place)} values={values} onChange={setValues} />
    {line.product && <FormGroup>
      <Label for="logistics-correct-supplier">Supplier</Label>
      <Input id="logistics-correct-supplier" type="select" value={supplier} onChange={event => setSupplier(event.target.value)}>
        {Object.entries(supplierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </Input>
    </FormGroup>}
    <FormGroup>
      <Label for="logistics-correct-notes">Notes</Label>
      <Input id="logistics-correct-notes" type="textarea" rows={2} maxLength={500} value={notes} onChange={event => setNotes(event.target.value)} />
    </FormGroup>
    <StockEffect movesStock={movesStock} moves={moves} line={line} balances={balances} />
    <ReasonField id="logistics-correct-reason" value={reason} onChange={setReason} placeholder="e.g. one can left at the warehouse" />
  </AfterShipmentDialog>
}

// A catalogue product put in the parcel after the validation (US 83): an active product of the event's sport, on the sheet or not (one a
// line holds comes as a line of its own, D21), with its quantities per place and its supplier. A product not on the sheet is chosen first.
export function CorrectAddLineDialog({ addable, movesStock, balances, pending, failure, onSubmit, onClose }) {
  const [initial] = useState(() => firstOffer(addable))
  const [key, setKey] = useState(initial?.key || "")
  const entry = addable.find(candidate => candidate.key === key)
  const places = entry?.places || [null]
  const [values, setValues] = useState({})
  const [supplier, setSupplier] = useState(initial?.supplier || "mpg")
  const [notes, setNotes] = useState("")
  const [reason, setReason] = useState("")
  const choose = value => {
    setKey(value)
    setValues({})
    setSupplier(addable.find(candidate => candidate.key === value)?.supplier || "mpg")
  }
  const quantities = places.map(place => ({ place, quantity: Number(values[place ?? ""] || 0) }))
  const valid = Boolean(entry) && places.every(place => !values[place ?? ""] || whole(values[place ?? ""])) && quantities.some(({ quantity }) => quantity > 0)
  const moves = valid && supplier === "mpg" ? quantities.filter(({ quantity }) => quantity).map(({ place, quantity }) => ({ place, quantity: -quantity })) : []
  const submit = () => onSubmit({ action: "correctAddLine", line: key, quantities, supplier, ...(notes.trim() ? { notes: notes.trim() } : {}), reason: reason.trim() })
  return <AfterShipmentDialog id="logistics-correct-add-title" title="Add product after shipment" dirty={Object.values(values).some(Boolean) || Boolean(notes.trim() || reason.trim())}
    failure={failure} onClose={onClose} hint={addable.length > 0 && reasonHint(reason, "add the product")}
    actions={addable.length > 0 && <Button color="primary" disabled={pending || !valid || !reason.trim()} onClick={submit}>{pending ? "Adding…" : "Add to the parcel"}</Button>}>
    {addable.length ? <>
      <FormGroup>
        <Label for="logistics-correct-add-product">Product</Label>
        <Input id="logistics-correct-add-product" type="select" value={key} onChange={event => choose(event.target.value)}>
          {addable.map(candidate => <option key={candidate.key} value={candidate.key}>{offeredName(candidate.product.name, candidate.onSheet)}</option>)}
        </Input>
        <FormText>Active products for the event's sport, on the sheet or not.</FormText>
      </FormGroup>
      <PlaceQuantities id="logistics-correct-add-quantity" places={places} values={values} onChange={setValues} />
      <FormGroup>
        <Label for="logistics-correct-add-supplier">Supplier</Label>
        <Input id="logistics-correct-add-supplier" type="select" value={supplier} onChange={event => setSupplier(event.target.value)}>
          {Object.entries(supplierNames).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </Input>
      </FormGroup>
      <FormGroup>
        <Label for="logistics-correct-add-notes">Notes</Label>
        <Input id="logistics-correct-add-notes" type="textarea" rows={2} maxLength={500} value={notes} onChange={event => setNotes(event.target.value)} />
      </FormGroup>
      <StockEffect movesStock={movesStock} moves={moves} line={entry} balances={balances} />
      <ReasonField id="logistics-correct-add-reason" value={reason} onChange={setReason} placeholder="e.g. added to the parcel before the pick-up" />
    </> : <p className="mb-0">The catalogue has no active product for the event's sport.</p>}
  </AfterShipmentDialog>
}

// A line taken out of the parcel after the validation (US 83): what MPG sent of it comes back to the stock.
export function CorrectRemoveLineDialog({ line, name, movesStock, balances, pending, failure, onSubmit, onClose }) {
  const [reason, setReason] = useState("")
  const moves = lockedPlaces(line).map(({ place, quantity }) => ({ place, quantity: sentBy(line.supplier, line, quantity) })).filter(({ quantity }) => quantity)
  return <AfterShipmentDialog id="logistics-correct-remove-title" title={`Remove line after shipment — ${name}`} dirty={Boolean(reason.trim())} failure={failure} onClose={onClose}
    hint={reasonHint(reason, "remove the line")} actions={<Button color="danger" disabled={pending || !reason.trim()} onClick={() => onSubmit({ action: "correctRemoveLine", line: line.key, reason: reason.trim() })}>
      {pending ? "Removing…" : "Remove"}
    </Button>}>
    <p className="mb-2">{`The line leaves the sheet and the shipment. ${lockedLabel(line)}.`}</p>
    <StockEffect movesStock={movesStock} moves={moves} line={line} balances={balances} />
    <ReasonField id="logistics-correct-remove-reason" value={reason} onChange={setReason} placeholder="e.g. not in the parcel" />
  </AfterShipmentDialog>
}

// How a shipment left, corrected (US 84, 86): an AWB entered by hand with its courier, parcels, link and day, or the note and day
// of a shipment without a courier. The transport stays; another one means cancelling the shipment and validating it again. The
// reason is optional (T5, Tudor 2026-10-06): the action logs keep the values before and after.
export function CorrectTransportDialog({ shipment, pending, failure, onSubmit, onClose }) {
  const [initial] = useState(() => transportForm(shipment))
  const [form, setForm] = useState(initial)
  const [reason, setReason] = useState("")
  // The weight set after weighing (US 96): corrected, or cleared back to the products' weight, with the same reason (Tudor, 2026-10-01).
  const initialWeight = shipment.setWeight != null ? String(shipment.setWeight) : ""
  const [weight, setWeight] = useState(initialWeight)
  const weighed = statedWeight(weight)
  const weightProblem = weight.trim() !== "" && weighed == null
  const weightChanged = weight.trim() !== initialWeight
  const change = field => event => {
    const { value } = event.target
    setForm(previous => ({ ...previous, [field]: value }))
  }
  // A day kept as it was validated stays valid; a new one is today or earlier, in Romania.
  const today = todayIn(() => new Date())
  const { problems, valid } = checkTransport(form, form.date === initial.date ? null : today)
  const changed = JSON.stringify(transportBody(form)) !== JSON.stringify(transportBody(initial)) || weightChanged
  const body = { action: "correctDetails", ...transportBody(form), ...(weightChanged ? { weight: weighed } : {}), reason: reason.trim() }
  return <AfterShipmentDialog id="logistics-correct-transport-title" title={`Correct the shipment — ${shipmentName(shipment)}`} size="lg" dirty={changed || Boolean(reason.trim())}
    failure={failure} onClose={onClose}
    actions={<Button color="primary" disabled={pending || !valid || weightProblem || !changed}
      onClick={() => onSubmit(body)}>{pending ? "Saving…" : "Save correction"}</Button>}>
    <p className="small text-muted">{shipment.transport === "noCourier" ? "Without courier: correct the note, the day or the weight."
      : "An AWB entered by hand is corrected with its courier, parcels, link, day and weight. To send it another way, cancel the shipment and validate it again."}</p>
    <TransportFields form={form} change={change} problems={problems} today={today} />
    <FormGroup>
      <Label for="logistics-correct-weight">Weight after weighing (kg)</Label>
      <Input id="logistics-correct-weight" type="number" min="0.01" max={SHIPMENT_WEIGHT_LIMIT} step="0.01" value={weight} invalid={weightProblem}
        onChange={event => setWeight(event.target.value)} />
      <FormFeedback>{`Enter a weight from 0.01 to ${SHIPMENT_WEIGHT_LIMIT} kg, or leave it empty.`}</FormFeedback>
      <FormText>{`Empty: the products' weight, ${weightLabel(productsWeightOf(shipment)) || "none"}.`}</FormText>
    </FormGroup>
    <ReasonField id="logistics-correct-transport-reason" optional value={reason} onChange={setReason} placeholder="e.g. the AWB was mistyped" />
  </AfterShipmentDialog>
}

const RETURN_REASONS = ["Unused, returned by the club", "Parcel returned by the courier", "Event cancelled"]
// A return (US 93) or its correction (US 94): what came back of each item the shipment sent. A return adds what came back now; its
// correction states the total that really came back, and the difference is recorded. The shipment keeps its content.
export function ReturnDialog({ shipment, correcting = false, pending, failure, onSubmit, onClose }) {
  const returned = returnedTotals(shipment)
  const items = sentItems(shipment)
  const [values, setValues] = useState(() => Object.fromEntries(items.map(item => [itemKey(item), correcting ? String(returned[itemKey(item)] || 0) : ""])))
  const [preset, setPreset] = useState(correcting ? "other" : RETURN_REASONS[0])
  const [other, setOther] = useState("")
  const rows = items.map(item => {
    const key = itemKey(item)
    const value = values[key]
    const already = returned[key] || 0
    const limit = correcting ? item.quantity : item.quantity - already
    const quantity = value === "" ? 0 : Number(value)
    const problem = value !== "" && (!whole(value) || quantity > limit)
      ? (correcting ? `At most ${quantityLabel(item.quantity, item.unit)} were sent.` : `At most ${quantityLabel(limit, item.unit)} can come back.`) : null
    return { item, key, already, quantity, problem }
  })
  const reason = preset === "other" ? other.trim() : preset
  const chosen = correcting ? rows.filter(({ quantity, already }) => quantity !== already) : rows.filter(({ quantity }) => quantity > 0)
  const valid = rows.every(({ problem }) => !problem) && chosen.length > 0 && Boolean(reason)
  const submit = () => onSubmit({ action: correcting ? "correctReturn" : "recordReturn",
    items: chosen.map(({ item, quantity }) => ({ productId: item.product.id, place: item.place, quantity })), reason })
  const column = correcting ? "Returned in total" : "Returned now"
  const typed = rows.some(({ quantity, already }) => (correcting ? quantity !== already : quantity > 0)) || Boolean(other.trim())
  return <AfterShipmentDialog id="logistics-return-title" title={`${correcting ? "Correct the return" : "Record return"} — ${shipment.kind === "supplementary" ? shipmentName(shipment) : "main shipment"}`} size="lg" dirty={typed}
    failure={failure} onClose={onClose} hint={reasonHint(reason, correcting ? "correct the return" : "record the return")}
    actions={<Button color="primary" disabled={pending || !valid} onClick={submit}>
      {pending ? "Saving…" : correcting ? "Save correction" : "Record return"}
    </Button>}>
    <Table size="sm" className="logistics-stack">
      <thead><tr><th>Product</th><th className="text-right">Shipped</th><th className="text-right">Already returned</th><th className="text-right">{column}</th></tr></thead>
      <tbody>{rows.map(({ item, key, already, problem }) => <tr key={key}>
        <td>{itemName(item)}</td>
        <td className="text-right" data-label="Shipped">{quantityLabel(item.quantity, item.unit)}</td>
        <td className="text-right" data-label="Already returned">{already ? quantityLabel(already, item.unit) : "—"}</td>
        <td className="text-right" data-label={column}>
          <Input type="number" min={0} step={1} bsSize="sm" aria-label={`${column}: ${itemName(item)}`} value={values[key]} invalid={Boolean(problem)}
            onChange={event => setValues({ ...values, [key]: event.target.value })} />
        </td>
      </tr>)}</tbody>
    </Table>
    {rows.filter(({ problem }) => problem).map(({ key, problem }) => <p key={key} className="small text-danger mb-1">{problem}</p>)}
    {correcting ? <ReasonField id="logistics-return-reason" value={other} onChange={setOther} placeholder="e.g. recorded on the wrong event" />
      : <FormGroup className="mb-0">
        <Label for="logistics-return-reason">Reason <span className="text-danger">*</span></Label>
        <Input id="logistics-return-reason" type="select" value={preset} onChange={event => setPreset(event.target.value)}>
          {RETURN_REASONS.map(text => <option key={text} value={text}>{text}</option>)}
          <option value="other">Other…</option>
        </Input>
        {preset === "other" && <Input className="mt-2" aria-label="Other reason" maxLength={500} value={other} onChange={event => setOther(event.target.value)} />}
      </FormGroup>}
    <p className="small text-muted mt-2 mb-0">The shipment keeps its content. The stock takes back what came back; the event's consumption is what was shipped
      less what was returned. To send again, create a supplementary shipment.</p>
  </AfterShipmentDialog>
}

// Refusals the sheet answers by changing: the preview is read again before another try.
const STALE = ["LOGISTICS_REVISION_CONFLICT"]

// „Cancel shipment” (US 91–92, D17): its preview shows what goes back to the stock and what came back, which keeps the shipment. The sheet
// returns to Ready for processing, its quantities locked: Reopen sheet changes them. The cancellation is bound to the preview's revision.
export function CancelShipmentDialog({ eventId, pending, onSubmit, onClose }) {
  const [preview, setPreview] = useState({ loading: true })
  const [attempt, setAttempt] = useState(0)
  const [reason, setReason] = useState("")
  const [failure, setFailure] = useState(null)
  useEffect(() => {
    let active = true
    setPreview(previous => ({ ...previous, loading: true, error: false }))
    get(`/admin/logistics/events/${eventId}/shipments/cancellation`).then(response => { if (active) setPreview({ data: response.data }) })
      .catch(() => { if (active) setPreview({ error: true }) })
    return () => { active = false }
  }, [eventId, attempt])
  const reread = () => { setFailure(null); setAttempt(value => value + 1) }
  const data = preview.data
  const items = list => list.map(item => `${itemName(item)} ${quantityLabel(item.quantity, unitOf(data.shipment, item))}`).join(" · ")
  const submit = () => {
    setFailure(null)
    onSubmit({ action: "cancelShipment", revision: data.revision, reason: reason.trim() }).then(result => {
      if (result && !["saved", "cancelled"].includes(result.status)) setFailure(result)
    })
  }
  return <AfterShipmentDialog id="logistics-cancel-title" title="Cancel shipment — main" size="lg" closeLabel="Keep the shipment" onClose={onClose}
    dirty={Boolean(reason.trim())} hint={data && reasonHint(reason, "cancel the shipment")}
    failure={failure && <>{failure.status === "unknown" ? "Events has not confirmed the cancellation yet; it may still be saved. Check the action logs before trying again."
      : failure.message}{STALE.includes(failure.code) && <> <Button color="link" className="p-0 align-baseline" onClick={reread}>Read the sheet again</Button></>}</>}
    actions={<Button color="danger" disabled={pending || !data || data.returned.length > 0 || !reason.trim()} onClick={submit}>
      {pending ? "Cancelling…" : "Cancel shipment"}
    </Button>}>
    {preview.loading && !data && <p role="status">Reading what the cancellation does…</p>}
    {preview.error && <Alert color="danger">The cancellation could not be read. <Button color="link" className="p-0 align-baseline" onClick={reread}>Retry</Button></Alert>}
    {data && <>
      {data.returned.length > 0 && <Alert color="danger" className="small">
        {`Goods came back from this shipment (${items(data.returned)}): correct the return to 0 before cancelling it.`}
      </Alert>}
      <p className="mb-2">The main shipment returns to “Ready for processing”: the sheet stays validated, its quantities locked, and the warehouse processes
        it again, with a new AWB. To change the quantities, reopen the sheet. Supplementary shipments are not affected; none is created until the
        main shipment is shipped again.</p>
      {data.shipment.awb && <p className="mb-2">{`AWB ${data.shipment.awb} becomes free for another shipment.`}</p>}
      {data.shipment.awbSource === "generated" && <p className="mb-2 text-warning">{GENERATED_CANCELLATION}</p>}
      <p className="mb-1"><strong>Back to the stock</strong></p>
      <p className="small">{data.stock.length ? items(data.stock) : beforeInventory}</p>
      <p className="small text-muted">The contact person and the organizer receive the cancellation email.</p>
      <ReasonField id="logistics-cancel-reason" value={reason} onChange={setReason} placeholder="e.g. the courier never picked it up" />
    </>}
  </AfterShipmentDialog>
}
